import Vue from 'vue'
import { defaultLoopParameters, LoopParameters, type LoopType } from './parameters/LoopParameters'
import { defaultVisualParameters, type VisualParameters } from './parameters/VisualParameters'
import { defaultSoundParameters, SoundParameters, type SOUND_ACTIVE_TYPES, type SOUND_VOLUME_TYPES } from './parameters/SoundParameters'
import { type AbstractPlayableContent } from '../../playable/models/AbstractPlayableContent'
import { playerService } from './PlayerService'
import type { PlayerModeType, PlayerViewType } from './PlayerTypes'
import {midiDispatcher} from '../../midi/MidiDispatcher'
import audioPlayer from '../../synth/AudioPlayer'

export interface PlayerConfigState {
  speed: number // [0,1]
  loopParameters: LoopParameters,
  visualParameters: VisualParameters,
  soundParameters: SoundParameters,
  playerMode: PlayerModeType
  playerView: PlayerViewType
  textReturn: boolean
  validateSuggested: boolean // true once the teacher has suggested "Valider" at least once in this session
}

const defaultPlayerConfigState: PlayerConfigState = {
  playerMode: 'normal',
  playerView: 'piano',
  speed: 1,
  loopParameters: defaultLoopParameters,
  visualParameters: defaultVisualParameters,
  soundParameters: defaultSoundParameters,
  textReturn: localStorage.getItem('textReturn') === 'true' || true,
  validateSuggested: false,
}

class PlayerConfig {
  public readonly state = Vue.observable<PlayerConfigState>({...defaultPlayerConfigState})

  /* Player Config */
  public setPlayerConfigState(playerConfig: PlayerConfigState) {
    this.state.playerView = playerConfig.playerView
    this.state.playerMode = playerConfig.playerMode
    this.setSpeed(playerConfig.speed)
    this.state.loopParameters = playerConfig.loopParameters
    this.state.visualParameters = playerConfig.visualParameters
    this.state.soundParameters = playerConfig.soundParameters
    this.state.textReturn = playerConfig.textReturn || localStorage.getItem('textReturn') === 'true' || true
    this.state.validateSuggested = false
  }
  public resetPlayerConfigState() {
    this.state.playerView = defaultPlayerConfigState.playerView
    this.state.playerMode = defaultPlayerConfigState.playerMode
    this.setSpeed(defaultPlayerConfigState.speed)
    this.state.loopParameters = defaultPlayerConfigState.loopParameters
    this.state.visualParameters = defaultPlayerConfigState.visualParameters
    this.state.soundParameters = defaultPlayerConfigState.soundParameters
    this.state.textReturn = defaultPlayerConfigState.textReturn
    this.state.validateSuggested = false
  }

  /* Visibility Handling */
  public toggleTimelineContentVisibility(type: SOUND_ACTIVE_TYPES, isActive: boolean) {
    this.state.visualParameters.isTimelineContentsDisplayed[type] = !this.state.visualParameters.isTimelineContentsDisplayed[type]
    if (!(midiDispatcher.state.isActive && (type === "LEFT" || type === "RIGHT") && !isActive)) {
      this.setVolumeActive(type, !isActive)
    }
  }

  setSpeed(value: number, withSetTempo = true) {
    this.state.speed = Math.round(value * 100) / 100
    if(withSetTempo) {
      audioPlayer.emit('setTempoFromSpeed', value)
    }
  }

  /* Sound Handling */
  public setVolumeActive(type: SOUND_ACTIVE_TYPES, isActive: boolean) {
    this.state.soundParameters.actives[type] = isActive
  }
  public changeVolume(type: SOUND_VOLUME_TYPES, volume: number) {
    if (volume > 0 && !this.state.soundParameters.actives.MAIN) {
      this.setVolumeActive(type, true)
    }
    this.state.soundParameters.volumes[type] = volume;
    this.setVolumeActive(type, volume > 0)
  }
  public toggleVolume(type: SOUND_ACTIVE_TYPES) {
    const isTypeIsSoundVolumeType = type === 'LEFT' || type === 'RIGHT' || type === 'MELODY'
    if (isTypeIsSoundVolumeType && this.state.soundParameters.editableVolumes.includes(type) && this.state.soundParameters.volumes[type] === 0 && !this.state.soundParameters.actives[type]) {
      this.state.soundParameters.volumes[type] = this.state.soundParameters.defaultVolumes[type];
    }

    const activesSoundParameters = Object.keys(this.state.soundParameters.actives) as SOUND_ACTIVE_TYPES[]
    if (type === 'MAIN' && !activesSoundParameters.some((isActive) => isActive)) {
      activesSoundParameters.forEach((key) => {
        this.state.soundParameters.actives[key] = !this.state.soundParameters.actives.MAIN;
      });
    }
    else {
      this.setVolumeActive(type, !this.state.soundParameters.actives[type])
    }
  }

  /* Loop Handling */
  public setLoop(type: LoopType, playableContent: AbstractPlayableContent) {
    this.state.loopParameters = new LoopParameters(type, playableContent)
  }
  public setLoopWithTimer(type: LoopType, timer: number) {
    if(!playerService.state.playerPlayableContent) {
      return
    }

    let playableContent: AbstractPlayableContent

    switch (type) {
      case 'PLAY':
      case 'NONE':
        playableContent = playerService.state.playerPlayableContent;
        break;
      case 'RHYTHMIC_BLOC':
        console.error('setLoopWithTimer on BR not implemented yet...');
        return;
      case 'PART':
        const partIndex = playerService.state.divisions[timer].partIndex;
        playableContent = playerService.state.playerPlayableContent.playableTimeline[partIndex];
        break;
      case 'SECTION':
        const sectionIndex = playerService.state.divisions[timer].sectionId;
        playableContent = playerService.state.playerPlayableContent.playableTimeline[sectionIndex];
        break;
    }

    this.setLoop(type, playableContent!)
  }
  public setTextReturn(value: boolean) {
    this.state.textReturn = value
    localStorage.setItem('textReturn', value.toString())
  }
  public setVisualParameters(visualParameters: VisualParameters) {
    this.state.visualParameters = visualParameters
  }
}

export const playerConfig = new PlayerConfig()
